import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import useGoogleAnalytics from './hooks/useGoogleAnalytics';
import usePageTracking from './hooks/usePageTracking';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import NotificationToastContainer from './components/NotificationToastContainer';

// Pages
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import ProfilePage from './pages/ProfilePage';
import LikedMedia from './pages/LikedMedia';
import EditProfile from './pages/EditProfile';
import MediaViewer from './pages/MediaViewer';
import MediaDisplay from './pages/MediaDisplay';
import AlbumDisplay from './pages/AlbumDisplay';
import TheWave from './pages/TheWave';
import Dashboard from './pages/Dashboard';
import Calendar from './pages/Calendar';
import Discover from './pages/Discover';
import Feed from './pages/Feed';
import ArtistFeed from './pages/ArtistFeed';
import Gallery from './pages/Gallery';
import Player from './pages/Player';
import ArtistGallery from './pages/ArtistGallery';
import AdminDashboard from './pages/AdminDashboard';
import UserManagement from './pages/UserManagement';
import EventManagement from './pages/EventManagement';
import EventForm from './pages/EventForm';
import EventDetails from './pages/EventDetails';
import CrewManagement from './pages/CrewManagement';
import HighlightedArtistsAdmin from './pages/HighlightedArtistsAdmin';
import FeaturedSongsManagement from './pages/FeaturedSongsManagement';
import AnalyticsSettings from './pages/AnalyticsSettings';
import AdminAnalytics from './pages/AdminAnalytics';
import AdminStorage from './pages/AdminStorage';
import SiteSettings from './pages/SiteSettings';
import PostViewer from './pages/PostViewer';
import BadgeManagement from './pages/BadgeManagement';
import PaymentsManagement from './pages/PaymentsManagement';
import AdminEmail from './pages/AdminEmail';
import AdminBandMessage from './pages/AdminBandMessage';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Support from './pages/Support';
import Following from './pages/Following';
import Subscriptions from './pages/Subscriptions';
import Account from './pages/Account';
import Settings from './pages/Settings';
import Notifications from './pages/Notifications';
import NotificationsManagement from './pages/NotificationsManagement';
import { NotificationProvider } from './context/NotificationContext';
import Sitemap from './pages/Sitemap';
import NotFound from './pages/NotFound';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="loading-spinner w-8 h-8 mx-auto mb-4"></div>
                    <p className="text-gray-400">Loading...</p>
                </div>
            </div>
        );
    }

    return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
    const { isAuthenticated, user, loading, isAdmin } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="loading-spinner w-8 h-8 mx-auto mb-4"></div>
                    <p className="text-gray-400">Loading...</p>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    // Check admin status - also allow user IDs 6 and 7 as fallback (TheDeepWave admin)
    if (!isAdmin() && user?.id !== 6 && user?.id !== 7) {
        console.log('Admin check failed:', { 
            isAdmin: isAdmin(), 
            userId: user?.id, 
            userIsAdmin: user?.is_admin,
            username: user?.username 
        });
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

// Public Route Component (redirect to dashboard if authenticated)
const PublicRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="loading-spinner w-8 h-8 mx-auto mb-4"></div>
                    <p className="text-gray-400">Loading...</p>
                </div>
            </div>
        );
    }

    return isAuthenticated ? <Navigate to="/dashboard" replace /> : children;
};

// Open Route Component (accessible to everyone, no redirect)
const OpenRoute = ({ children }) => {
    return children;
};

// Main App Component
const AppContent = () => {
    const { loading } = useAuth();
    
    // Initialize Google Analytics and internal page tracking
    useGoogleAnalytics();
    usePageTracking();

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="text-6xl mb-4 animate-float">🌊</div>
                    <div className="loading-spinner w-8 h-8 mx-auto mb-4"></div>
                    <p className="text-gray-400">Loading The Deep Wave...</p>
                </div>
            </div>
        );
    }

    return (
        <NotificationProvider>
        <div className="min-h-screen bg-gray-900 flex flex-col overflow-x-hidden">
            <ScrollToTop />
            <Navbar />
            <NotificationToastContainer />
            <div className="h-16"></div>
            <main className="flex-1">
                <Routes>
                    {/* Public Routes */}
                    <Route 
                        path="/" 
                        element={
                            <OpenRoute>
                                <LandingPage />
                            </OpenRoute>
                        } 
                    />
                    <Route 
                        path="/login" 
                        element={
                            <PublicRoute>
                                <Login />
                            </PublicRoute>
                        } 
                    />
                    <Route
                        path="/register"
                        element={
                            <PublicRoute>
                                <Register />
                            </PublicRoute>
                        }
                    />
                    <Route
                        path="/forgot-password"
                        element={
                            <OpenRoute>
                                <ForgotPassword />
                            </OpenRoute>
                        }
                    />
                    <Route
                        path="/reset-password"
                        element={
                            <OpenRoute>
                                <ResetPassword />
                            </OpenRoute>
                        }
                    />

                    {/* Subscription Plans Page (Open Access) */}
                    <Route 
                        path="/subscriptions" 
                        element={
                            <OpenRoute>
                                <Subscriptions />
                            </OpenRoute>
                        } 
                    />

                    {/* Legal/Info Pages (Open Access) */}
                    <Route 
                        path="/privacy" 
                        element={
                            <OpenRoute>
                                <Privacy />
                            </OpenRoute>
                        } 
                    />
                    <Route 
                        path="/terms" 
                        element={
                            <OpenRoute>
                                <Terms />
                            </OpenRoute>
                        } 
                    />
                    <Route 
                        path="/support" 
                        element={
                            <OpenRoute>
                                <Support />
                            </OpenRoute>
                        } 
                    />
                    <Route
                        path="/sitemap"
                        element={
                            <OpenRoute>
                                <Sitemap />
                            </OpenRoute>
                        }
                    />

                    {/* Protected Routes */}
                    <Route 
                        path="/dashboard" 
                        element={
                            <ProtectedRoute>
                                <Dashboard />
                            </ProtectedRoute>
                        } 
                    />
                    <Route 
                        path="/discover" 
                        element={
                            <ProtectedRoute>
                                <Discover />
                            </ProtectedRoute>
                        } 
                    />
                    <Route path="/feed" element={<Feed />} />
                    <Route path="/feed/:filter" element={<Feed />} />
                    <Route path="/feed/artist/:username" element={<ArtistFeed />} />
                    <Route path="/feed/artist/:username/:filter" element={<ArtistFeed />} />
                    <Route 
                        path="/following" 
                        element={
                            <ProtectedRoute>
                                <Following />
                            </ProtectedRoute>
                        } 
                    />

                    {/* Settings Route */}
                    <Route
                        path="/settings"
                        element={
                            <ProtectedRoute>
                                <Settings />
                            </ProtectedRoute>
                        }
                    />

                    {/* Notifications Route */}
                    <Route
                        path="/notifications"
                        element={
                            <ProtectedRoute>
                                <Notifications />
                            </ProtectedRoute>
                        }
                    />

                    {/* Account Management Route */}
                    <Route
                        path="/account"
                        element={
                            <ProtectedRoute>
                                <Account />
                            </ProtectedRoute>
                        }
                    />

                    {/* Profile Routes (public but enhanced when authenticated) */}
                    <Route path="/profile/:username" element={<ProfilePage />} />
                    <Route path="/profile/:username/liked" element={<LikedMedia />} />
                    <Route 
                        path="/edit-profile" 
                        element={
                            <ProtectedRoute>
                                <EditProfile />
                            </ProtectedRoute>
                        } 
                    />

                    {/* Media Viewer Route (public) */}
                    <Route path="/media-viewer" element={<MediaViewer />} />
                    
                    {/* Media Display Routes (public) - enhanced media pages with metadata */}
                    <Route path="/view/:type/:username/:filename" element={<MediaDisplay />} />
                    
                    {/* Post Viewer Route (public) */}
                    <Route path="/view/post/:username/:postId" element={<PostViewer />} />

                    {/* Album Landing Page (public) */}
                    <Route path="/album/:username/:identifier" element={<AlbumDisplay />} />

                    {/* The Wave - Users Directory */}
                    <Route path="/community" element={<TheWave />} />

                    {/* Calendar Route (public) */}
                    <Route path="/calendar" element={<Calendar />} />
                    
                    {/* Event Details Route (public) */}
                    <Route path="/events/:eventId" element={<EventDetails />} />
                    
                    {/* Gallery Routes (public) */}
                    <Route path="/gallery" element={<Gallery />} />
                    <Route path="/gallery/artist/:username" element={<ArtistGallery />} />

                    {/* Player Route (public) */}
                    <Route path="/radio" element={<Player />} />

                    {/* Admin Routes */}
                    <Route 
                        path="/admin" 
                        element={
                            <AdminRoute>
                                <AdminDashboard />
                            </AdminRoute>
                        } 
                    />
                    <Route 
                        path="/admin/dashboard" 
                        element={
                            <AdminRoute>
                                <AdminDashboard />
                            </AdminRoute>
                        } 
                    />
                    <Route 
                        path="/admin/events" 
                        element={
                            <AdminRoute>
                                <EventManagement />
                            </AdminRoute>
                        } 
                    />
                    <Route 
                        path="/admin/events/create" 
                        element={
                            <AdminRoute>
                                <EventForm />
                            </AdminRoute>
                        } 
                    />
                    <Route 
                        path="/admin/events/:eventId/edit" 
                        element={
                            <AdminRoute>
                                <EventForm />
                            </AdminRoute>
                        } 
                    />
                    <Route 
                        path="/admin/users" 
                        element={
                            <AdminRoute>
                                <UserManagement />
                            </AdminRoute>
                        } 
                    />
                    <Route 
                        path="/admin/crew" 
                        element={
                            <AdminRoute>
                                <CrewManagement />
                            </AdminRoute>
                        } 
                    />
                    <Route 
                        path="/admin/highlighted-artists" 
                        element={
                            <AdminRoute>
                                <HighlightedArtistsAdmin />
                            </AdminRoute>
                        } 
                    />
                    <Route 
                        path="/admin/featured-songs" 
                        element={
                            <AdminRoute>
                                <FeaturedSongsManagement />
                            </AdminRoute>
                        } 
                    />
                    <Route
                        path="/admin/analytics"
                        element={
                            <AdminRoute>
                                <AdminAnalytics />
                            </AdminRoute>
                        }
                    />
                    <Route
                        path="/admin/analytics/settings"
                        element={
                            <AdminRoute>
                                <AnalyticsSettings />
                            </AdminRoute>
                        }
                    />
                    <Route
                        path="/admin/storage"
                        element={
                            <AdminRoute>
                                <AdminStorage />
                            </AdminRoute>
                        }
                    />
                    <Route 
                        path="/admin/settings" 
                        element={
                            <AdminRoute>
                                <SiteSettings />
                            </AdminRoute>
                        } 
                    />
                    <Route 
                        path="/admin/badges" 
                        element={
                            <AdminRoute>
                                <BadgeManagement />
                            </AdminRoute>
                        } 
                    />
                    <Route
                        path="/admin/payments"
                        element={
                            <AdminRoute>
                                <PaymentsManagement />
                            </AdminRoute>
                        }
                    />
                    <Route
                        path="/admin/email"
                        element={
                            <AdminRoute>
                                <AdminEmail />
                            </AdminRoute>
                        }
                    />
                    <Route
                        path="/admin/band-message"
                        element={
                            <AdminRoute>
                                <AdminBandMessage />
                            </AdminRoute>
                        }
                    />
                    <Route
                        path="/admin/notifications"
                        element={
                            <AdminRoute>
                                <NotificationsManagement />
                            </AdminRoute>
                        }
                    />

                    {/* Catch-all route for 404 */}
                    <Route path="/404" element={<NotFound />} />
                    <Route path="*" element={<Navigate to="/404" replace />} />
                </Routes>
            </main>
            <Footer />
        </div>
        </NotificationProvider>
    );
};

// Root App Component with Router and Providers
function App() {
    return (
        <Router>
            <AuthProvider>
                <AppContent />
            </AuthProvider>
        </Router>
    );
}

export default App;